import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import type {
  SubscriptionAccessResponse,
  ErrorDetails,
  TuitionErrorData
} from '@/types/subscription'
import { API_ROUTES } from '@/const/apiRoutes'
import { TUITION_ERROR_STATUS_CODES } from '@/config/tuition'
import { DEFAULT_APP_PLATFORM } from '@/const/platformAccess'
import { createTuitionError, normalizeTuitionErrorCode } from '@/utils/tuitionError'

export type CheckStatusOptions = {
  /** When true, avoid overwriting access state on transient API failures (e.g. on /permission-denied). */
  preserveStateOnNetworkError?: boolean
}

/** Router guard: skip refetch while a successful status response is fresh (aligned with Launchpad). */
const STATUS_CACHE_TTL_MS = 60_000

export const useSubscriptionStore = defineStore('subscription', () => {
  // State
  const data = ref<SubscriptionAccessResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastChecked = ref<number | null>(null)
  const isInitialized = ref(false)
  const platforms = ref<string[]>([])
  /**
   * `has_access` is true but this app's platform code is missing from `platforms`.
   */
  const platformAccessDenied = ref(false)

  // Getters
  const hasAccess = computed(() => data.value?.has_access === true)

  const errorDetails = computed<ErrorDetails | null>(() => {
    if (!error.value) return null

    try {
      const parsed = JSON.parse(error.value) as Partial<ErrorDetails> & {
        error?: string
      }
      return {
        error: normalizeTuitionErrorCode(parsed.error),
        message: parsed.message ?? '',
        action: parsed.action ?? '',
        action_url: parsed.action_url ?? '',
        status: parsed.status
      }
    } catch {
      return null
    }
  })

  // Serialized tuition error (JSON) → errorDetails
  const hasError = computed(() => errorDetails.value !== null)

  const message = computed(() => errorDetails.value?.message || '')

  const actionButtonText = computed(() => errorDetails.value?.action || '')

  const actionUrl = computed(() => errorDetails.value?.action_url || '')

  function hasPlatform(code: string) {
    return platforms.value.includes(code)
  }

  function syncPlatformAccessDenied() {
    platformAccessDenied.value =
      hasAccess.value && !hasPlatform(DEFAULT_APP_PLATFORM)
  }

  // Actions
  async function checkStatus(options?: CheckStatusOptions): Promise<boolean> {
    loading.value = true

    try {
      const response = await api.get<SubscriptionAccessResponse>(
        API_ROUTES.SUBSCRIPTION_ACCESS_STATUS
      )

      const responseData = response.data
      platforms.value = Array.isArray(responseData.platforms)
        ? responseData.platforms
        : []

      if (responseData.has_access) {
        data.value = responseData
        error.value = null
        syncPlatformAccessDenied()
        lastChecked.value = Date.now()
        isInitialized.value = true
        return true
      } else {
        platformAccessDenied.value = false
        data.value = responseData

        const errorData = createTuitionError(responseData)
        error.value = JSON.stringify(errorData)

        lastChecked.value = Date.now()
        isInitialized.value = true
        return false
      }
    } catch (err: any) {
      if (
        err.response &&
        TUITION_ERROR_STATUS_CODES.includes(err.response.status)
      ) {
        const detail = err.response.data?.detail
        const errorData = createTuitionError(
          detail,
          'Unable to verify tuition status'
        )

        platforms.value = []
        platformAccessDenied.value = false
        data.value = null
        error.value = JSON.stringify(errorData)

        lastChecked.value = Date.now()
        isInitialized.value = true
        return false
      }

      console.error('Tuition access check failed:', err)

      if (!options?.preserveStateOnNetworkError && !isInitialized.value) {
        error.value = JSON.stringify({
          error: 'tuition_required',
          message: 'Unable to verify tuition status. Please try again.',
          action: 'Retry',
          action_url: '/tuition/manage'
        })
      }

      return hasAccess.value
    } finally {
      loading.value = false
    }
  }

  function reset() {
    data.value = null
    loading.value = false
    error.value = null
    lastChecked.value = null
    isInitialized.value = false
    platforms.value = []
    platformAccessDenied.value = false
  }

  // Error payload helpers
  function setError(errorData: TuitionErrorData) {
    error.value = JSON.stringify(errorData)
  }

  function clearError() {
    error.value = null
  }

  /** True if the last successful server response is still within TTL (for router guard). */
  function isCacheFresh() {
    return (
      isInitialized.value &&
      lastChecked.value != null &&
      Date.now() - lastChecked.value < STATUS_CACHE_TTL_MS
    )
  }

  return {
    // State
    data,
    loading,
    error,
    lastChecked,
    isInitialized,

    // Getters
    hasAccess,
    errorDetails,
    hasError,
    message,
    actionButtonText,
    actionUrl,
    platforms,
    platformAccessDenied,

    // Actions
    checkStatus,
    isCacheFresh,
    reset,
    setError,
    clearError,
    hasPlatform
  }
})
